import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";

/**
 * CRYSTAL PALACE transmitting station (London) — the 219 m (718 ft) self-supporting
 * steel lattice tower, the tallest free-standing lattice structure in the UK and
 * London's tallest until One Canada Square (1990). First transmission 28 March
 * 1956. Built from published data (Wikipedia):
 *   - 120 ft (37 m) square base, four legs.
 *   - Rises in TWELVE diminishing panels to a 14.5 ft (4.4 m) square platform at
 *     429 ft (131 m) — the self-supporting lattice.
 *   - Above that, a slim antenna mast continues to the 219 m top.
 *
 * A natural two-`segment` tower: a wide 12-panel tapering lattice base + a slender
 * antenna mast. 1950s structural steel (~BS 15 ≈ A36).
 */

// Square base 37 m → circumradius (faces=4) = 37/√2 ≈ 26.2 m.
// 4.4 m platform → circumradius 4.4/√2 ≈ 3.1 m.
const LATTICE_LEG: Section = { name: "Crystal Palace lattice leg", A: 0.03, I: 1.2e-3, dragWidth: 0.35 };
const MAST_LEG: Section = { name: "Crystal Palace antenna-mast leg", A: 0.006, I: 4e-5, dragWidth: 0.16 };

export const design: TowerDesign = {
  id: "crystal-palace",
  name: "Crystal Palace Tower (London)",
  description: "219 m self-supporting steel lattice TV transmitter (1956), UK's tallest lattice tower.",
  author: "iconic tower — from published spec (Wikipedia)",
  spec: {
    type: "lattice",
    height: 219,
    segments: {
      faces: 4,
      baseRadius: 26.2, // 37 m square base
      sections: [
        {
          toHeight: 131, // 429 ft — top of the self-supporting lattice
          topRadius: 3.1, // 4.4 m square platform
          panels: 12, // the twelve diminishing panels
          legSection: LATTICE_LEG,
          braceSection: "angleL6",
          legMaterial: "a36", // 1950s structural steel
          braceMaterial: "a36",
          bracing: "X",
        },
        {
          toHeight: 219, // the slim antenna mast to the top
          topRadius: 0.8,
          panels: 8,
          legSection: MAST_LEG,
          braceSection: "braceMed",
          legMaterial: "a36",
          braceMaterial: "a36",
          bracing: "X",
        },
      ],
    },
    foundation: {
      soil: "stiff-clay", // London Clay
      base: { type: "pad", width: 8, diameter: 1, depth: 4, concrete: "c25" },
    },
  },
};
